import React, { useEffect, useState } from 'react';
import './AdminDashboard.css';
import SeatingChart from './SeatingChart';

const AdminDashboard = () => {
  /* Existing state */
  const [messages, setMessages] = useState([]);
  const [rsvps, setRsvps] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedTab, setSelectedTab] = useState('messages'); // 'messages', 'rsvps', 'resumen', 'mesas', 'regalos'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' = Newest first, 'asc' = Oldest first

  const API_URL = window.location.hostname === 'localhost' ? "http://localhost:5001" : "https://esponjoboda.com";

  const toggleSort = () => {
    setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
  };

  const sortData = (data) => {
    return [...data].sort((a, b) => {
      const dateA = new Date(a.timestamp);
      const dateB = new Date(b.timestamp);
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });
  };

  const sortedMessages = sortData(messages);
  const sortedRsvps = sortData(rsvps);


  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setLoading(true);
    const token = localStorage.getItem('adminToken');

    if (!token) {
      window.location.href = '/login';
      return;
    }

    try {
      const headers = { 'Authorization': `Bearer ${token}` };

      const [messagesRes, rsvpsRes, purchasesRes, productsRes] = await Promise.all([
        fetch(`${API_URL}/api/messages`, { headers }),
        fetch(`${API_URL}/api/rsvps`, { headers }),
        fetch(`${API_URL}/api/purchases`, { headers }),
        fetch(`${API_URL}/api/products`)
      ]);

      if (messagesRes.status === 401 || rsvpsRes.status === 401) {
        localStorage.removeItem('adminToken');
        window.location.href = '/login';
        return;
      }

      const messagesData = await messagesRes.json().catch(() => ({ messages: [] }));
      const rsvpsData = await rsvpsRes.json().catch(() => ({ rsvps: [] }));
      const purchasesData = await purchasesRes.json().catch(() => ({ purchases: [] }));
      const productsData = await productsRes.json().catch(() => ({ products: [] }));

      const rawRsvps = rsvpsData.rsvps || [];
      const latestRsvpsMap = new Map();
      rawRsvps.forEach(rsvp => {
        // Normalize the key to prevent duplicates from trailing spaces or case differences
        const codeKey = rsvp.code ? String(rsvp.code).trim().toLowerCase() : '';
        const nameKey = rsvp.name ? String(rsvp.name).trim().toLowerCase() : '';
        // Prioritize code if available, otherwise fallback to name
        const key = codeKey || nameKey;
        
        const existing = latestRsvpsMap.get(key);
        if (!existing || new Date(rsvp.timestamp) > new Date(existing.timestamp)) {
          latestRsvpsMap.set(key, rsvp);
        }
      });
      const uniqueRsvps = Array.from(latestRsvpsMap.values());

      setMessages(messagesData.messages || []);
      setRsvps(uniqueRsvps);
      setPurchases(purchasesData.purchases || []);
      setProducts(productsData.products || []);
    } catch (err) {
      console.error('Error fetching admin data:', err);
      setError('Error al cargar los datos. Asegúrate de que el token de administrador esté configurado correctamente en el Worker.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="loading">Cargando...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="admin-dashboard">
      <h1>Panel de Administración</h1>

      <div className="admin-tabs">
        <button
          className={`tab-btn ${selectedTab === 'messages' ? 'active' : ''}`}
          onClick={() => setSelectedTab('messages')}
        >
          Mensajes ({messages.length})
        </button>
        <button
          className={`tab-btn ${selectedTab === 'rsvps' ? 'active' : ''}`}
          onClick={() => setSelectedTab('rsvps')}
        >
          RSVPs ({rsvps.length})
        </button>
        <button
          className={`tab-btn ${selectedTab === 'resumen' ? 'active' : ''}`}
          onClick={() => setSelectedTab('resumen')}
        >
          📋 Resumen de Invitados
        </button>
        <button
          className={`tab-btn ${selectedTab === 'mesas' ? 'active' : ''}`}
          onClick={() => setSelectedTab('mesas')}
        >
          🪑 Mesas
        </button>
        <button
          className={`tab-btn ${selectedTab === 'regalos' ? 'active' : ''}`}
          onClick={() => setSelectedTab('regalos')}
        >
          🎁 Regalos ({purchases.length})
        </button>
      </div>

      {selectedTab === 'messages' && (
        <section className="admin-section">
          <div className="section-header-row">
            <h2>Mensajes de sus invitados</h2>
            <button className="sort-btn" onClick={toggleSort}>
              Orden: {sortOrder === 'desc' ? 'Más recientes' : 'Más antiguos'} {sortOrder === 'desc' ? '⬇' : '⬆'}
            </button>
          </div>
          <div className="messages-list">
            {sortedMessages.length === 0 && <p className="no-data">No hay mensajes aún</p>}
            {sortedMessages.map((message, index) => (
              <div key={index} className="message-item">
                <div className="message-header">
                  <strong>{message.name}</strong>
                  <span className="message-date">{new Date(message.timestamp).toLocaleDateString('es-MX')}</span>
                </div>
                <p className="message-content">{message.message}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {selectedTab === 'rsvps' && (
        <section className="admin-section">
          <div className="section-header-row">
            <h2>Confirmaciones</h2>
            <button className="sort-btn" onClick={toggleSort}>
              Orden: {sortOrder === 'desc' ? 'Más recientes' : 'Más antiguos'} {sortOrder === 'desc' ? '⬇' : '⬆'}
            </button>
          </div>
          <div className="rsvp-summary">
            <div className="summary-card">
              <h3>Asistirán</h3>
              <span className="count">
                {rsvps.filter(r => r.attending).reduce((acc, curr) => {
                  // guestsNames is an array like ["+3"] or []
                  const guestValue = curr.guestsNames && curr.guestsNames[0] ? curr.guestsNames[0] : '';
                  const extraGuests = guestValue ? parseInt(guestValue.replace('+', ''), 10) : 0;
                  return acc + 1 + (isNaN(extraGuests) ? 0 : extraGuests);
                }, 0)}
              </span>
            </div>
            <div className="summary-card">
              <h3>No asistirán</h3>
              <span className="count">{rsvps.filter(r => !r.attending).length}</span>
            </div>
          </div>
          <div className="rsvp-list">
            {sortedRsvps.length === 0 && <p className="no-data">No hay confirmaciones aún</p>}
            {sortedRsvps.map((rsvp, index) => {
              // guestsNames is an array like ["+3"] or []
              const guestValue = rsvp.guestsNames && rsvp.guestsNames[0] ? rsvp.guestsNames[0] : '';
              const extraGuests = guestValue ? parseInt(guestValue.replace('+', ''), 10) : 0;
              const totalPeople = 1 + (isNaN(extraGuests) ? 0 : extraGuests);
              const drinkschoice = rsvp.drinks;

              return (
                <div key={index} className="rsvp-item">
                  <div className="rsvp-header">
                    <strong>{rsvp.name}</strong>
                    <span className={`status ${rsvp.attending ? 'attending' : 'not-attending'}`}>
                      {rsvp.attending ? 'Asistiré' : 'No Asistiré'}
                    </span>
                  </div>
                  {rsvp.attending && (
                    <div className="rsvp-details">
                      <p>Total: {totalPeople} persona{totalPeople > 1 ? 's' : ''}</p>
                      {extraGuests > 0 && <p className="plus-one-detail">Incluye: +{extraGuests} invitados extra</p>}
                      {drinkschoice && (
                        <p className="rsvp-drinks">
                          <strong>Bebida:</strong> {drinkschoice}
                        </p>
                      )}
                    </div>
                  )}
                  <span className="rsvp-date">{new Date(rsvp.timestamp).toLocaleDateString('es-MX')}</span>
                </div>
              )
            })}
          </div>
        </section>
      )}

      {selectedTab === 'resumen' && (
        <section className="admin-section admin-section--wide">
          <div className="section-header-row">
            <h2>Resumen y Conteo de Invitados</h2>
          </div>
          <p className="section-description">
            Aquí puedes ver el desglose exacto para confirmar que la suma coincida (Total: {
              rsvps.filter(r => r.attending).reduce((acc, curr) => {
                const guestValue = curr.guestsNames && curr.guestsNames[0] ? curr.guestsNames[0] : '';
                const extraGuests = guestValue ? parseInt(guestValue.replace('+', ''), 10) : 0;
                return acc + 1 + (isNaN(extraGuests) ? 0 : extraGuests);
              }, 0)
            } asistentes confirmados).
          </p>
          <div className="resumen-table-container">
            <table className="resumen-table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Código</th>
                  <th>Estado</th>
                  <th>Total de Personas</th>
                  <th>Fecha de RSVP</th>
                </tr>
              </thead>
              <tbody>
                {sortedRsvps.map((rsvp, index) => {
                  const guestValue = rsvp.guestsNames && rsvp.guestsNames[0] ? rsvp.guestsNames[0] : '';
                  const extraGuests = guestValue ? parseInt(guestValue.replace('+', ''), 10) : 0;
                  const totalPeople = rsvp.attending ? 1 + (isNaN(extraGuests) ? 0 : extraGuests) : 0;
                  
                  return (
                    <tr key={index} className={rsvp.attending ? 'row-attending' : 'row-not-attending'}>
                      <td>{rsvp.name}</td>
                      <td>{rsvp.code || '-'}</td>
                      <td>
                        <span className={`status ${rsvp.attending ? 'attending' : 'not-attending'}`}>
                          {rsvp.attending ? 'Asistirá' : 'No Asistirá'}
                        </span>
                      </td>
                      <td>{totalPeople > 0 ? totalPeople : '-'}</td>
                      <td>{new Date(rsvp.timestamp).toLocaleDateString('es-MX')}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <th colSpan="3" style={{ textAlign: 'right' }}>Total de Asistentes:</th>
                  <th>
                    {rsvps.filter(r => r.attending).reduce((acc, curr) => {
                      const guestValue = curr.guestsNames && curr.guestsNames[0] ? curr.guestsNames[0] : '';
                      const extraGuests = guestValue ? parseInt(guestValue.replace('+', ''), 10) : 0;
                      return acc + 1 + (isNaN(extraGuests) ? 0 : extraGuests);
                    }, 0)}
                  </th>
                  <th></th>
                </tr>
              </tfoot>
            </table>
          </div>
        </section>
      )}
      {selectedTab === 'mesas' && (
        <section className="admin-section admin-section--wide">
          <h2>Mapa de Mesas</h2>
          <p className="section-description">
            Configura el salón y asigna invitados a cada asiento haciendo clic en los lugares.
          </p>
          <SeatingChart rsvps={rsvps} />
        </section>
      )}

      {selectedTab === 'regalos' && (
        <section className="admin-section admin-section--wide">
          <div className="section-header-row">
            <h2>Regalos Comprados</h2>
          </div>
          <div className="resumen-table-container">
            <table className="resumen-table">
              <thead>
                <tr>
                  <th>Regalo</th>
                  <th>Precio</th>
                  <th>Comprador</th>
                  <th>Email</th>
                  <th>Fecha</th>
                </tr>
              </thead>
              <tbody>
                {purchases.length === 0 && (
                  <tr>
                    <td colSpan="5" className="no-data" style={{textAlign: 'center', padding: '1rem'}}>No hay compras registradas</td>
                  </tr>
                )}
                {purchases.map((purchase, index) => {
                  const product = products.find(p => p.id === purchase.productId) || { name: `Producto #${purchase.productId}`, price: purchase.amountTotal ? purchase.amountTotal / 100 : 0 };
                  
                  return (
                    <tr key={index}>
                      <td>{product.name}</td>
                      <td>${product.price.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN</td>
                      <td>{purchase.buyerName || purchase.customerName || 'Anónimo'}</td>
                      <td>{purchase.buyerEmail || purchase.customerEmail || '-'}</td>
                      <td>{new Date(purchase.timestamp).toLocaleDateString('es-MX')}</td>
                    </tr>
                  )
                })}
              </tbody>
              <tfoot>
                <tr>
                  <th style={{ textAlign: 'right' }}>Total Recibido:</th>
                  <th style={{ textAlign: 'left' }}>
                    ${purchases.reduce((acc, purchase) => {
                      const product = products.find(p => p.id === purchase.productId);
                      const price = product ? product.price : (purchase.amountTotal ? purchase.amountTotal / 100 : 0);
                      return acc + price;
                    }, 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN
                  </th>
                  <th colSpan="3"></th>
                </tr>
              </tfoot>
            </table>
          </div>
        </section>
      )}

    </div>
  );
};

export default AdminDashboard;